<script lang="ts" setup>
  import isEmpty from 'lodash-es/isEmpty';
  import { computed, useAttrs, watch } from 'vue';
  import { CurrencyDisplay, CurrencyInputOptions, useCurrencyInput } from 'vue-currency-input';

  import Icon from '../Icon/Icon.vue';
  import Input, { InputProps } from '../Input/Input.vue';

  const CURRENCY_INPUT_BASE_OPTIONS: CurrencyInputOptions = {
    currency: 'USD',
    currencyDisplay: CurrencyDisplay.hidden,
    precision: 2,
    hideCurrencySymbolOnFocus: true,
    hideGroupingSeparatorOnFocus: true,
    hideNegligibleDecimalDigitsOnFocus: false,
    autoDecimalDigits: true,
    useGrouping: true,
    accountingSign: false,
  };

  export interface CurrencyInputProps extends Omit<InputProps, 'modelValue' | 'type'> {
    inputOptions?: Partial<CurrencyInputOptions>;
    modelValue?: string | null;
    hidePrepend?: boolean;
  }

  const props = withDefaults(defineProps<CurrencyInputProps>(), {
    modelValue: null,
    errorText: undefined,
    hintText: undefined,
    label: undefined,
    id: undefined,
    inputOptions: undefined,
    hidePrepend: false,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the input value changes.
     */
    (e: 'update:model-value', v: CurrencyInputProps['modelValue']): void;
  }>();

  const attrs = useAttrs();

  const currencyInputOptions = computed(() => {
    return {
      ...CURRENCY_INPUT_BASE_OPTIONS,
      ...(!isEmpty(props.inputOptions) ? props.inputOptions : {}),
    };
  });

  const { inputRef: inputEl, setOptions, setValue, numberValue } = useCurrencyInput(currencyInputOptions.value, false);

  defineExpose({ inputEl });

  watch(
    () => numberValue.value,
    (newValue: number | null | string) => {
      let stringValue: string | null;

      if (Object.is(newValue, -0)) {
        if (typeof currencyInputOptions.value.precision === 'number') {
          stringValue = '-0.' + '0'.repeat(currencyInputOptions.value.precision);
        } else {
          stringValue = '-0.' + '0'.repeat(currencyInputOptions.value.precision?.min || 0);
        }
      } else {
        stringValue = typeof newValue === 'number' ? newValue.toFixed(2) : newValue;
      }

      emit('update:model-value', stringValue);
    },
  );

  watch(
    () => props.modelValue,
    (newValue: number | null | string) => {
      let numberValue: number | null;

      if (typeof newValue === 'string') {
        numberValue = parseFloat(newValue || '0');
      } else {
        numberValue = newValue;
      }

      setValue(numberValue);
    },
  );

  watch(
    () => props.inputOptions,
    () => {
      setOptions(currencyInputOptions.value);
    },
  );

  const inputAttrs = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { inputOptions: _, ...propsWithoutCurrencyInputOptions } = props;

    return { ...propsWithoutCurrencyInputOptions, ...attrs } as typeof attrs & InputProps;
  });
</script>

<template>
  <Input
    v-bind="inputAttrs"
    ref="inputEl"
    class="stash-currency-input"
    type="text"
    data-test="stash-currency-input"
    inputmode="decimal"
  >
    <template v-if="!hidePrepend" #prepend>
      <Icon name="sign-dollar" />
    </template>
  </Input>
</template>
